import { PlanMode } from 'mbs-ui-shared@models/PlanTypes.enum';
import { BaseForStepsHelper, WizardHelpersSettings } from 'wizards/helpers/bases/base-for-steps-helper';
import { RestoreStepsDataFromPlanHelper } from 'wizards/helpers/restore-steps-data-from-plan-helper/restore-steps-data-from-plan-helper';
import { StepsDataFromPlanHelper } from 'wizards/helpers/steps-data-from-plan-helper/steps-data-from-plan-helper';
import { SupportMethodsForStepsFromPlan } from 'wizards/helpers/support-methods-for-steps-from-plan';
import { RestoreIbbFormModel } from 'wizards/models/restoreIBB/restore-ibb-form-model';
import {
  InstantRestoreTypes,
  RestoreAsDiskEnum,
  RestoreTestType,
  RestoreToHyperVTypes,
  RestoreToVMNeedHostTypes,
  RestoreToVMTypes
} from 'wizards/models/type-data-models';

export class RestoreIbbModelForStepHelpers extends BaseForStepsHelper {
  public static RestoreIBBModel: RestoreIbbFormModel;

  public static RestoreIBBModelFromPlan(plan, settings: WizardHelpersSettings = this.DefaultSettings): RestoreIbbFormModel {
    this.RestoreIBBModel = new RestoreIbbFormModel(settings.isNBF);
    const newPlan = plan && (plan.plan || plan.Plan);
    if (newPlan) {
      this.setThisValues(plan, settings);
      this.setStorageType(settings.mode, newPlan.ConnectionID);
      this.updatePlanNameStep(newPlan, settings.compName);
      this.updateWhereBackupStep(newPlan);

      const { backupPlanName, restorePoint } = this.getBackupNameAndPoint(newPlan);

      this.RestoreIBBModel.computerToRestoreStep.computerName = newPlan.RestoreSourcePrefix || '';
      this.RestoreIBBModel.restorePointStep = RestoreStepsDataFromPlanHelper.updateRestorePoint(
        newPlan,
        false,
        restorePoint,
        settings.timeZoneOffset
      );
      this.RestoreIBBModel.fileFormats = RestoreStepsDataFromPlanHelper.updateFileFormats(plan.VirtualDiskFileFormatList);
      this.RestoreIBBModel.typeDataStep = RestoreStepsDataFromPlanHelper.updateTypeDataStep(newPlan, this.planType);

      this.updateAzureOrEC2AndTemporary(this.RestoreIBBModel.typeDataStep.restoreAs, newPlan, this.RestoreIBBModel);

      this.RestoreIBBModel.backupToRestoreStep = RestoreStepsDataFromPlanHelper.updateBackupToRestore(
        newPlan,
        this.planType,
        backupPlanName
      );

      this.RestoreIBBModel.diskItems = newPlan.DiskInfo;
      // TODO Correct the DiskVolumes field when the backend with the desired field is ready
      this.RestoreIBBModel.partitions = RestoreStepsDataFromPlanHelper.updatePartitions(plan.DiskVolumes);
      this.RestoreIBBModel.partitionsStep = RestoreStepsDataFromPlanHelper.updatePartitionsStep(newPlan, plan.DiskVolumes);

      if (InstantRestoreTypes.includes(this.RestoreIBBModel.typeDataStep.restoreAs)) {
        // Only Proxmox carries host credentials; a DR Server plan reports its own test type and has none
        const isProxmox = newPlan.TestVmSettings?.TestType === RestoreTestType.Proxmox;

        if (isProxmox) this.RestoreIBBModel.selectHostStep = StepsDataFromPlanHelper.updateHostForInstantRestore(newPlan);

        this.RestoreIBBModel.vmSettingsStep = StepsDataFromPlanHelper.updateVmSettingsForInstantRestore(newPlan, isProxmox);
        this.updateFormIfExistInitialData(settings, this.RestoreIBBModel);

        return this.RestoreIBBModel;
      }

      if (!this.isCreate && RestoreToVMTypes.includes(this.RestoreIBBModel.typeDataStep.restoreAs)) {
        const isProxmox = this.RestoreIBBModel.typeDataStep.restoreAs === RestoreAsDiskEnum.RestoreToProxmox;
        if (RestoreToVMNeedHostTypes.includes(this.RestoreIBBModel.typeDataStep.restoreAs)) {
          this.RestoreIBBModel.selectHostStep = StepsDataFromPlanHelper.updateSelectHostIBB(newPlan, !this.isCreate, isProxmox);
        }

        if (RestoreToHyperVTypes.includes(this.RestoreIBBModel.typeDataStep.restoreAs)) {
          this.RestoreIBBModel.vmSettingsStep = StepsDataFromPlanHelper.updateVmSettingsHyperVStep(newPlan);
        } else if (this.RestoreIBBModel.typeDataStep.restoreAs === RestoreAsDiskEnum.RestoreToVMwareVSphere) {
          this.RestoreIBBModel.vmSettingsStep = StepsDataFromPlanHelper.updateVmSettingsStep(newPlan);
        } else {
          this.RestoreIBBModel.vmSettingsStep = StepsDataFromPlanHelper.updateVmSettingsStep(newPlan, true);
        }
      } else {
        this.RestoreIBBModel.destinationStep = RestoreStepsDataFromPlanHelper.updateDestinationStep(newPlan, false, plan.DiskVolumes);
      }

      this.RestoreIBBModel.encryptionOptionsStep = RestoreStepsDataFromPlanHelper.updateEncryptionOptionsStep(
        newPlan,
        settings.mode === PlanMode.create,
        this.isNBF
      );
      this.RestoreIBBModel.scheduleStep = StepsDataFromPlanHelper.updateSchedule(
        newPlan,
        plan.OverdueAfterSuccess,
        this.isCreate,
        this.isUnix,
        true
      );
      this.RestoreIBBModel.prePostActionsStep = StepsDataFromPlanHelper.updatePrePostActions(newPlan, true);
      this.RestoreIBBModel.notificationStep = StepsDataFromPlanHelper.updateNotification(newPlan.Notification);
    }

    this.updateFormIfExistInitialData(settings, this.RestoreIBBModel);

    return this.RestoreIBBModel;
  }

  public static updatePlanNameStep(newPlan, compName: string): void {
    const name = newPlan.Name || SupportMethodsForStepsFromPlan.generatePlanName(compName, this.planType, this.timeZoneOffset);
    this.RestoreIBBModel.planNameStep = RestoreStepsDataFromPlanHelper.updatePlanNameStep(name, newPlan.DeletePlan);
  }
  public static updateWhereBackupStep(newPlan): void {
    this.RestoreIBBModel.whereBackupStep = StepsDataFromPlanHelper.updateWhereBackup(
      newPlan.IsHybridBackup,
      newPlan.HybridID,
      newPlan.ConnectionID
    );
    this.RestoreIBBModel.Storages = this.storages;
  }

  private static updateAzureOrEC2AndTemporary(restoreAs: RestoreAsDiskEnum, newPlan, formModel): void {
    if (restoreAs === RestoreAsDiskEnum.AVM || restoreAs === RestoreAsDiskEnum.ADD) {
      formModel.azureData = RestoreStepsDataFromPlanHelper.updateAzureData(newPlan, formModel.azureData);
      formModel.azureStep = RestoreStepsDataFromPlanHelper.updateAzureStep(newPlan);
    } else if (restoreAs === RestoreAsDiskEnum.EBSVolume || restoreAs === RestoreAsDiskEnum.EC2) {
      formModel.EC2Data = RestoreStepsDataFromPlanHelper.updateEC2Data(newPlan, formModel.EC2Data);
      formModel.EC2 = RestoreStepsDataFromPlanHelper.updateEC2(newPlan);
      formModel.temporaryData = RestoreStepsDataFromPlanHelper.updateTemporaryData(newPlan, formModel.temporaryData);
      formModel.temporaryInstanceStep = RestoreStepsDataFromPlanHelper.updateTemporaryInstance(newPlan);
    }
  }
}
